import React, { useCallback, useEffect, useRef, useState } from 'react'

interface SelectionFeedbackInputProps {
  onSubmit: (feedback: string) => void
  onCancel: () => void
  placeholder?: string
}

/**
 * Inline feedback input that appears in the bubble menu when user wants to provide
 * feedback on selected text. Matches Figma design with auto-expanding textarea and
 * submit button anchored to bottom-right.
 */
export function SelectionFeedbackInput({
  onSubmit,
  onCancel,
  placeholder = 'Add your feedback...'
}: SelectionFeedbackInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-focus textarea when mounted
  // The processing decoration persists the visual selection even when editor loses focus
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Auto-resize textarea as content grows
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto'
      // Set to scrollHeight, capped by max-height via CSS
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [value])

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim()
    if (trimmed) {
      onSubmit(trimmed)
      setValue('')
    }
  }, [value, onSubmit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    },
    [handleSubmit, onCancel]
  )

  return (
    <div className='flex items-center gap-2 w-[360px] max-h-[340px] p-4 rounded-[24px] border border-tokens-status-link-1 bg-white shadow-popover'>
      {/* Text area that auto-expands up to container max, then scrolls */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={1}
        className='flex-1 bg-transparent border-none outline-none resize-none font-geist font-medium text-sm leading-[22px] text-tokens-neutral-text-primary placeholder:text-tokens-neutral-text-tertiary overflow-y-auto max-h-[276px]'
      />
      {/* Submit button - dark background per Figma */}
      <button
        type='button'
        onClick={handleSubmit}
        disabled={!value.trim()}
        className={`
          flex items-center justify-center w-8 h-8 rounded-xl transition-colors shrink-0 self-end text-white
          ${
            value.trim()
              ? 'bg-tokens-neutral-bg-action hover:opacity-80 cursor-pointer'
              : 'bg-tokens-neutral-bg-disabled cursor-not-allowed'
          }
        `}
        aria-label='Submit feedback'
      >
        <ArrowUpIcon />
      </button>
    </div>
  )
}

function ArrowUpIcon() {
  return (
    <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
      <path
        d='M6 10V2M6 2L2.5 5.5M6 2L9.5 5.5'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}
